// src/lib/pillFit.js — B614. How many pills fit on a card-footer row.
//
// Lives in lib/ rather than beside the component for two reasons, one of them mechanical:
// `node --test` cannot import a `.jsx` file (ERR_UNKNOWN_FILE_EXTENSION), so a rule that stays
// in the component is a rule that cannot be tested. The other is the repo's standing split —
// `coopMath`, `deskFeed`, `binder-rules`, `match-score`: the rules are pure and tested, the
// rendering is verified by eye.
//
// This split is the only part of the overflow behaviour that can be wrong in a way nobody
// notices. A layout bug is visible the moment you look at a card; an off-by-one in the reserve
// arithmetic just quietly hides a pill that would have fitted, and nothing goes red.

// Width the `⋮` button occupies once it appears. Reserved when deciding the prefix, because a
// split that only fits by ignoring its own overflow button does not fit.
export const DOTS_W = 34

/**
 * @param widths  natural width of each pill, in DOM (= visual, within one group) order
 * @param avail   width the row actually has — NOT its content width; see the `flex-1 min-w-0`
 *                note in PillOverflow.jsx, which is what makes those two different numbers
 * @returns how many leading pills to render inline; the rest go into the `⋮`
 *
 * ⚠️ The `⋮` has to pay for itself: a pill only "fits" if the row can ALSO hold the overflow
 * button its own presence implies. The exception is the last pill, where fitting means there is
 * no overflow at all and therefore no button. Getting this wrong in the generous direction
 * produces a row that overflows by exactly the width of the control meant to prevent it.
 */
export function fitPrefix(widths, avail, gap = 6, dotsW = DOTS_W) {
  let used = 0, n = 0
  for (let i = 0; i < widths.length; i++) {
    const next = used + widths[i] + (i ? gap : 0)
    const needsDots = i < widths.length - 1
    if (next + (needsDots ? gap + dotsW : 0) > avail) break
    used = next; n = i + 1
  }
  // Never collapse everything: a row of nothing but a ⋮ tells an agent strictly less than one
  // pill and a ⋮ does, and at that point the card has bigger problems than this can solve.
  const shown = Math.max(1, n)
  // ⚠️ THE ⋮ MUST BUY SOMETHING. Owner, 2026-08-09, seeing the first build: *"no need three dot
  // for one link pill"* — and the arithmetic agrees. The button is 34 px and the pills it would
  // be hiding are 35–57 px, so folding exactly ONE pill away swaps a pill for a control of the
  // same width: it saves no space, costs a click, and hides an affordance behind a glyph that
  // names nothing. A ⋮ is only honest when it stands for two or more.
  // ⚠️ Applied AFTER the floor, not before: with two pills in a box that fits neither, the loop
  // returns n=0 and the floor lifts it to 1 — so the pill actually hidden is ONE, and testing
  // the pre-floor count would miss exactly the case the owner reported.
  if (widths.length - shown === 1) return widths.length
  return shown
}
